import { type BrowserOptions, type Event, type Exception, httpClientIntegration } from '@sentry/react';

import type { SentryOptions } from '@change-corgi/core/errorReporter/common';
import { getLocation } from '@change-corgi/core/window';

type Integration = Extract<NonNullable<BrowserOptions['integrations']>, readonly unknown[]>[number];

export type HttpClientConfig = NonNullable<SentryOptions['config']['httpClient']>;

// set by httpClientIntegration on the events it creates (auto.http.client.fetch, auto.http.client.xhr)
const HTTP_CLIENT_MECHANISM_PREFIX = 'auto.http.client.';

export function isIgnoredDomain(hostname: string, ignoredDomains: ReadonlyArray<string | RegExp>): boolean {
	return ignoredDomains.some((domain) => (typeof domain === 'string' ? domain === hostname : domain.test(hostname)));
}

function isIgnoredUrl(url: string, { ignoredDomains = [], ignoredPathPrefixes = [] }: HttpClientConfig): boolean {
	try {
		const { href: pageUrl, origin: pageOrigin } = getLocation();
		const { hostname, origin, pathname } = new URL(url, pageUrl);
		return (
			isIgnoredDomain(hostname, ignoredDomains) ||
			(origin === pageOrigin && ignoredPathPrefixes.some((prefix) => pathname.startsWith(prefix)))
		);
	} catch (e) {
		return false;
	}
}

function isHttpClientException({ mechanism }: Exception): boolean {
	// typed as required, but a throw here would drop the event, whatever it is
	const type: unknown = mechanism?.type;
	return typeof type === 'string' && type.startsWith(HTTP_CLIENT_MECHANISM_PREFIX);
}

/**
 * httpClientIntegration only supports an allow-list of request targets,
 * so failed requests to ignored domains/paths are dropped here instead
 */
export function ignoredHttpClientRequestsIntegration(config: HttpClientConfig): Integration {
	return {
		name: 'IgnoredHttpClientRequests',
		processEvent(event: Event): Event | null {
			const url = event.request?.url;
			const isHttpClientEvent = !!event.exception?.values?.some(isHttpClientException);
			return isHttpClientEvent && url && isIgnoredUrl(url, config) ? null : event;
		},
	};
}

export function genHttpClientIntegrations(config: SentryOptions['config']): Integration[] {
	return [
		httpClientIntegration(),
		...(config.httpClient ? [ignoredHttpClientRequestsIntegration(config.httpClient)] : []),
	];
}
